// pdp.js — PDP interaction layer. Server (Liquid) already rendered title/price/
// variants/body/JSON-LD. JS handles: size+material click, gallery swap, qty,
// frame upsell, add-to-cart (with optional frame line), free-ship bar.

import { cart, frameVariantForSize, registerQuickPool } from './app.js?v=32';
import { loadProducts, loadAllProducts, fmt, findVariant } from './data.js?v=23';
import { t, LOCALE } from './i18n.js?v=13';
const LP = LOCALE === 'nl' ? '' : '/' + LOCALE;

// Drempel per taal gelijk aan de winkelwagen (app.js): FR/ES 50, overige 30.
const FREE_SHIP = (LOCALE === 'fr' || LOCALE === 'es') ? 50 : 30;

// Frame line item — separate Shopify product handle. Wes vult later met echte handles per maat.
// Voor nu: render visueel als selectie; cart-line wordt als note/property toegevoegd zodat
// orders zichtbaar maken dat een lijst bij deze poster moet (fulfillment kiest juiste frame SKU).
const FRAME_SIZE_MAP = {
  // canonieke size code → frame metafield-key (matchen met data-frame-* in HTML)
  'XS': 'xs', 'S': 's', 'M': 'm', 'L': 'l',
  // raw Shopify option values → size code
  '30x40cm': 'xs', '30 x 40cm': 'xs',
  '50x70cm': 's',  '50 x 70cm': 's',  '51x71cm': 's',
  '60x90cm': 'm',  '60 x 90cm': 'm',  '61x91cm': 'm',  '90x60cm': 'm',
  '70x100cm': 'l', '70 x 100cm': 'l',
};

const state = {
  product:     null,
  materialGroup: 'paper',
  allProducts: [],
  size:        null,
  sizeRaw:     null,    // raw Shopify option (e.g. "60x90cm")
  material:    null,
  qty:         1,
  framePrice:  0,
  frameSelected: false,
  borderSelected: false,
};

// ============================================================
// LOAD product (inline JSON injected by Liquid template)
// ============================================================
async function loadProduct() {
  const pathMatch = location.pathname.match(/\/products\/([^/?#]+)/);
  const id = pathMatch ? pathMatch[1] : new URLSearchParams(location.search).get('id');
  const all = await loadProducts();
  const product = all.find(p => p.id === id || p.handle === id) || all[0];
  return { product, all };
}

// ============================================================
// Read initial size + material from active size-chip / material-tile
// (Liquid marks M as is-active by default)
// ============================================================
function readInitialVariant() {
  const activeSize = document.querySelector('#size-chips .size-chip.is-active')
                  || document.querySelector('#size-chips .size-chip:not(.is-pending)');
  const activeMat  = document.querySelector('#material-tiles .material-tile.is-active');
  if (activeSize) {
    activeSize.classList.add('is-active');
    state.size      = activeSize.dataset.size;
    state.sizeRaw   = activeSize.dataset.label;
    const d0 = chipData(activeSize);
    state.variantId = d0.vid || null;
    state.basePrice = d0.price || 0;
  }
  if (activeMat) {
    state.material = activeMat.dataset.material;
    state.materialGroup = activeMat.dataset.materialGroup || 'paper';
  }
  updateFramePriceLabel();
}

// ============================================================
// SIZE chips — click = update state + variant price.
// is-pending chips (geen echte Shopify variant) zijn niet klikbaar.
// ============================================================
function effectiveGroup() {
  if (state.materialGroup === 'dibond') return 'dibond';
  if ((state.materialGroup || 'paper') === 'paper' && state.borderSelected) return 'border';
  return state.materialGroup === 'border' ? 'border' : 'paper';
}

function chipData(c) {
  const g = effectiveGroup();
  return {
    vid: c.dataset[`${g}Vid`] || '',
    price: parseFloat(c.dataset[`${g}Price`]),
    cpa: parseFloat(c.dataset[`${g}Cpa`]),   // compare-at (leeg = geen sale voor deze variant)
    pending: c.dataset[`${g}Pending`] === 'true',
  };
}

// Doorstreepprijs + kortingspill per geselecteerde variant tonen/verbergen.
// Losstaand van de SSR-waarde (die was product-breed en bleef bij dibond/L plakken).
function updateSalePill(price, cpa) {
  const origEl = document.getElementById('pdp-price-orig');
  const pillEl = document.getElementById('pdp-sale-pill');
  // pas een korting tonen vanaf 1% (anders stond er bij een centverschil "-0%")
  const onSale = !isNaN(cpa) && cpa > price && Math.round(100 - (price / cpa) * 100) >= 1;
  if (origEl) {
    if (onSale) { origEl.textContent = `€ ${cpa.toFixed(2).replace('.', ',')}`; origEl.hidden = false; }
    else origEl.hidden = true;
  }
  if (pillEl) {
    if (onSale) { pillEl.textContent = `-${Math.round(100 - (price / cpa) * 100)}%`; pillEl.hidden = false; }
    else pillEl.hidden = true;
  }
}

function refreshChipPrices() {
  document.querySelectorAll('#size-chips .size-chip').forEach(c => {
    const d = chipData(c);
    const el = c.querySelector('.size-chip__price');
    if (el && !isNaN(d.price)) el.textContent = `€ ${d.price.toFixed(2).replace('.', ',')}`;
    c.classList.toggle('is-pending', d.pending);
  });
}

// aria-pressed spiegelen aan de is-active class zodat screenreaders de actieve
// maat/materiaal/kleur aankondigen.
function syncAria(selector) {
  document.querySelectorAll(selector).forEach(el =>
    el.setAttribute('aria-pressed', el.classList.contains('is-active') ? 'true' : 'false'));
}

function activateChip(c) {
  const group = c.closest('.size-chips');
  group.querySelectorAll('.size-chip').forEach(x => x.classList.remove('is-active'));
  c.classList.add('is-active');
  syncAria('#size-chips .size-chip');
  state.size = c.dataset.size;
  state.sizeRaw = c.dataset.label;
  const d = chipData(c);
  state.variantId = d.vid || null;
  const newPrice = d.price;
  if (!isNaN(newPrice)) {
    state.basePrice = newPrice;
    document.getElementById('pdp-price').textContent = `€ ${newPrice.toFixed(2).replace('.', ',')}`;
    document.getElementById('pdp-sticky-price').textContent =
      `€ ${(newPrice + (state.frameSelected ? state.framePrice : 0)).toFixed(2).replace('.', ',')}`;
    updateSalePill(newPrice, d.cpa);
    updateFreeShipBar(newPrice);
  }
  updateFramePriceLabel();
}

function setupSizeChips() {
  const chips = document.querySelectorAll('#size-chips .size-chip');
  chips.forEach(c => {
    c.addEventListener('click', (e) => {
      if (chipData(c).pending) {
        e.preventDefault();
        // korte hint flash
        c.animate(
          [{ transform: 'translateX(0)' }, { transform: 'translateX(-3px)' }, { transform: 'translateX(3px)' }, { transform: 'translateX(0)' }],
          { duration: 200, easing: 'ease-out' }
        );
        return;
      }
      activateChip(c);
    });
  });
}

// ============================================================
// MATERIAL tiles — click = update state (and ideally swap variant)
// ============================================================
function setupMaterialTiles() {
  const tiles = document.querySelectorAll('#material-tiles .material-tile');
  tiles.forEach(t => {
    t.addEventListener('click', () => {
      tiles.forEach(x => x.classList.remove('is-active'));
      t.classList.add('is-active');
      syncAria('#material-tiles .material-tile');
      state.material = t.dataset.material;
      state.materialGroup = t.dataset.materialGroup || 'paper';
      refreshChipPrices();
      // specificaties wisselen mee
      const specsGroup = state.materialGroup === 'dibond' ? 'dibond' : 'paper'; // border = papier
      document.querySelectorAll('[data-material-specs]').forEach(dl => {
        dl.hidden = dl.dataset.materialSpecs !== specsGroup;
      });
      // hero-foto wisselt mee met het materiaal (dibond-mockup indien gekoppeld)
      const tilesWrap = document.getElementById('material-tiles');
      const hero = document.getElementById('pdp-img');
      if (tilesWrap && hero) {
        const eg = effectiveGroup();
        const target = eg === 'dibond' ? tilesWrap.dataset.dibondImage
                     : eg === 'border' ? (tilesWrap.dataset.borderImage || tilesWrap.dataset.paperImage)
                     : tilesWrap.dataset.paperImage;
        if (target && hero.src !== target) hero.src = target;
      }
      // lijst-upsell alleen voor papier (dibond heeft eigen ophangsysteem) en niet voor L
      updateFrameVisibility();
      updateBorderToggleVisibility();
      // huidige maat herbevestigen (of eerste beschikbare)
      const cur = document.querySelector('#size-chips .size-chip.is-active');
      const target = (cur && !chipData(cur).pending) ? cur
        : [...document.querySelectorAll('#size-chips .size-chip')].find(c => !chipData(c).pending);
      if (target) activateChip(target);
    });
  });
  syncAria('#material-tiles .material-tile');
}

// ============================================================
// THUMBNAIL gallery
// ============================================================
function setupGallery() {
  const main = document.getElementById('pdp-img');
  const thumbs = () => [...document.querySelectorAll('#pdp-thumbs .pdp-thumb')];
  function activate(b) {
    if (!b) return;
    const src = b.dataset.src;
    if (src && main) main.src = src;
    thumbs().forEach(x => x.classList.remove('is-active'));
    b.classList.add('is-active');
    b.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }
  thumbs().forEach(b => b.addEventListener('click', () => activate(b)));
  // Swipe op de hoofdfoto bladert door de galerij (mobiel).
  if (main) {
    let sx = null, sy = null;
    main.addEventListener('touchstart', (e) => {
      sx = e.changedTouches[0].clientX; sy = e.changedTouches[0].clientY;
    }, { passive: true });
    main.addEventListener('touchend', (e) => {
      if (sx == null) return;
      const dx = e.changedTouches[0].clientX - sx;
      const dy = e.changedTouches[0].clientY - sy;
      sx = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return; // verticale scroll met rust laten
      const list = thumbs();
      const cur = Math.max(0, list.findIndex(x => x.classList.contains('is-active')));
      const next = (cur + (dx < 0 ? 1 : -1) + list.length) % list.length;
      activate(list[next]);
    }, { passive: true });
  }
}

// ============================================================
// QTY +/-
// ============================================================
function setupBundleTiers() {
  const tiers = document.querySelectorAll('#bundle-deal .bundle-tier');
  if (!tiers.length) return;
  tiers.forEach(t => t.addEventListener('click', () => {
    tiers.forEach(x => x.classList.remove('is-active'));
    t.classList.add('is-active');
    state.qty = parseInt(t.dataset.qty, 10) || 1;
    document.getElementById('pdp-qty').textContent = state.qty;
    updateFreeShipBar(state.basePrice);
  }));
  // qty +/- synct terug naar tiers
  const sync = () => {
    tiers.forEach(x => x.classList.toggle('is-active',
      parseInt(x.dataset.qty, 10) === Math.min(state.qty, 4)));
  };
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-action="pdp-inc"], [data-action="pdp-dec"]')) sync();
  });
}

function setupQty() {
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-action="pdp-inc"]')) {
      state.qty++;
      document.getElementById('pdp-qty').textContent = state.qty;
    }
    if (e.target.closest('[data-action="pdp-dec"]')) {
      state.qty = Math.max(1, state.qty - 1);
      document.getElementById('pdp-qty').textContent = state.qty;
    }
  });
}

// ============================================================
// FRAME upsell — toont prijs per geselecteerde maat,
// add-line toegevoegd bij cart-add
// ============================================================
function updateFrameVisibility() {
  const frame = document.getElementById('frame-upsell');
  if (!frame) return;
  // Dibond: geen lijst (blok weg). 70x100: blok blijft staan met uitleg, schakelaar uit (26-09-2026).
  const papier = ['paper', 'border'].includes(state.materialGroup || 'paper');
  const maatOk = state.size !== 'L';
  frame.hidden = !papier;
  const cb = document.getElementById('add-frame');
  const note = document.getElementById('frame-note');
  const card = document.querySelector('.frame-card');
  if (cb) cb.disabled = !maatOk;
  if (note) note.hidden = maatOk;
  card?.classList.toggle('is-disabled', !maatOk);
  if ((!papier || !maatOk) && state.frameSelected) {
    if (cb) cb.checked = false;
    state.frameSelected = false;
    card?.classList.remove('is-selected');
    refreshFrameTotals();
  }
}

function updateFramePriceLabel() {
  const wrap = document.querySelector('.frame-upsell');
  const label = document.getElementById('frame-price');
  if (!wrap || !label) return;
  const sizeKey = (state.size || 'M').toLowerCase();
  const price = parseFloat(wrap.dataset[`frame${sizeKey.charAt(0).toUpperCase() + sizeKey.slice(1)}`] || wrap.dataset.frameM || 19.99);
  state.framePrice = price;
  label.textContent = price.toFixed(2).replace('.', ',');
  const maat = document.getElementById('frame-maat');
  if (maat && state.sizeRaw) maat.textContent = String(state.sizeRaw).replace(/\s*x\s*/i, ' x ').replace(/\s*cm$/i, '') + ' cm';
  updateFrameVisibility();
}

// Totaal (sticky balk + gratis-verzendbalk) meteen bijwerken als de lijst aan/uit gaat
function refreshFrameTotals() {
  const sticky = document.getElementById('pdp-sticky-price');
  if (sticky && Number.isFinite(state.basePrice)) {
    sticky.textContent = `€ ${(state.basePrice + (state.frameSelected ? state.framePrice : 0)).toFixed(2).replace('.', ',')}`;
  }
  if (Number.isFinite(state.basePrice)) updateFreeShipBar(state.basePrice);
}

function setupFrameUpsell() {
  const checkbox = document.getElementById('add-frame');
  if (!checkbox) return;
  checkbox.addEventListener('change', () => {
    state.frameSelected = checkbox.checked;
    document.querySelector('.frame-card')?.classList.toggle('is-selected', checkbox.checked);
    refreshFrameTotals();
  });
}

// ============================================================
// FREE-SHIP bar
// ============================================================
function updateFreeShipBar(price) {
  const fill = document.getElementById('pdp-fship-fill');
  const label = document.getElementById('pdp-fship-label');
  if (!fill || !label) return;
  const total = price * state.qty + (state.frameSelected ? state.framePrice : 0);
  const pct = Math.min(100, (total / FREE_SHIP) * 100);
  fill.style.width = pct + '%';
  if (total >= FREE_SHIP) {
    label.innerHTML = t('cart_ship_done');
    fill.parentElement.parentElement.classList.add('is-free');
  } else {
    label.innerHTML = t('cart_ship_left', { amount: fmt(FREE_SHIP - total) });
    fill.parentElement.parentElement.classList.remove('is-free');
  }
}

// ============================================================
// ADD TO CART — pakt huidige variant, voegt frame als note toe
// ============================================================
function setupAddToCart() {
  const add = async () => {
    const p = state.product;
    // gebruik echte variant ID uit de actieve size chip (server-side gerendered)
    const vid = state.variantId ? Number(state.variantId) : null;
    if (!vid) {
      // geen echte variant — toon hint
      alert('Deze maat is binnenkort beschikbaar. Kies een andere maat of stuur ons een mail via de contactpagina.');
      return;
    }

    await cart.add({
      key:        `${p.id}-${vid}`,
      variantId:  vid,
      title:      p.title,
      image:      p.image,
      price:      state.basePrice,
      qty:        state.qty,
      size:       state.size,
      material:   state.material || 'paper',
      properties: null,
    });

    // Lijst = eigen product-regel (variant per maat), zodat hij echt wordt afgerekend
    const frame_vid = state.frameSelected ? frameVariantForSize(state.size || state.sizeRaw) : null;
    if (frame_vid) {
      const fvid = frame_vid;
      await cart.add({
        key:        `frame-${fvid}`,
        variantId:  fvid,
        title:      'Premium Zwarte Fotolijst',
        image:      '',
        price:      state.framePrice,
        qty:        state.qty,
        size:       state.size,
        properties: { 'Voor': `${p.title} (${state.sizeRaw || state.size})` },
      });
    }
    if (window.openCart) window.openCart();
  };
  document.getElementById('add-to-cart')?.addEventListener('click', add);
  document.getElementById('add-to-cart-sticky')?.addEventListener('click', add);
}

// ============================================================
// STICKY add-to-cart op mobile
// ============================================================
function updateBorderToggleVisibility() {
  const wrap = document.getElementById('border-toggle-wrap');
  if (!wrap) return;
  const paper = (state.materialGroup || 'paper') === 'paper';
  wrap.hidden = !paper;
  if (!paper && state.borderSelected) {
    state.borderSelected = false;
    const cb = document.getElementById('border-toggle');
    if (cb) cb.checked = false;
  }
}

function setupBorderToggle() {
  const cb = document.getElementById('border-toggle');
  if (!cb) return;
  cb.addEventListener('change', () => {
    state.borderSelected = cb.checked;
    refreshChipPrices();
    // huidige maat opnieuw activeren zodat variant-id/prijs/hero meegaan
    const cur = document.querySelector('#size-chips .size-chip.is-active');
    const target = (cur && !chipData(cur).pending) ? cur
      : [...document.querySelectorAll('#size-chips .size-chip')].find(c => !chipData(c).pending);
    if (target) activateChip(target);
    updateHeroForMaterial();
  });
}

function updateHeroForMaterial() {
  const tilesWrap = document.getElementById('material-tiles');
  const hero = document.getElementById('pdp-img');
  if (!tilesWrap || !hero) return;
  const eg = effectiveGroup();
  const target = eg === 'dibond' ? tilesWrap.dataset.dibondImage
               : eg === 'border' ? (tilesWrap.dataset.borderImage || tilesWrap.dataset.paperImage)
               : tilesWrap.dataset.paperImage;
  if (target && hero.src !== target) hero.src = target;
}

// ===== Kleur/Stijl-pills: wisselt varianten + hero per kleurwaarde =====
function normSize(t) { return String(t || '').toLowerCase().replace(/×/g, 'x').replace(/\s|cm/g, ''); }

function classifyMat(mat) {
  const m = String(mat || '').toLowerCase();
  if (m.includes('dibond')) return 'dibond';
  if (m.includes('rand')) return 'border';
  return 'paper';
}

function resolveChipsForColor() {
  const pills = document.getElementById('color-pills');
  const variants = state.product?.variants;
  if (!pills || !variants) return;
  const color = state.colorValue;
  document.querySelectorAll('#size-chips .size-chip').forEach(chip => {
    const want = normSize(chip.dataset.label);
    const alt = want.split('x').reverse().join('x');
    for (const g of ['paper', 'dibond', 'border']) {
      const v = variants.find(x => {
        if (classifyMat(x.material) !== g) return false;
        const vs = normSize(x.size);
        if (vs !== want && vs !== alt) return false;
        return !color || !x.opt3 || x.opt3 === color;
      });
      if (v) {
        chip.dataset[`${g}Vid`] = v.id;
        chip.dataset[`${g}Price`] = v.price;
        chip.dataset[`${g}Pending`] = 'false';
      } else {
        chip.dataset[`${g}Vid`] = '';
        chip.dataset[`${g}Pending`] = 'true';
      }
    }
  });
  refreshChipPrices();
  const cur = document.querySelector('#size-chips .size-chip.is-active');
  const target = (cur && !chipData(cur).pending) ? cur
    : [...document.querySelectorAll('#size-chips .size-chip')].find(c => !chipData(c).pending);
  if (target) activateChip(target);
}

function setupColorPills() {
  const pills = document.getElementById('color-pills');
  if (!pills) return;
  const first = pills.querySelector('.color-pill.is-active');
  state.colorValue = first?.dataset.color || null;
  syncAria('#color-pills .color-pill');
  pills.addEventListener('click', (e) => {
    const pill = e.target.closest('.color-pill');
    if (!pill) return;
    pills.querySelectorAll('.color-pill').forEach(x => x.classList.remove('is-active'));
    pill.classList.add('is-active');
    syncAria('#color-pills .color-pill');
    state.colorValue = pill.dataset.color;
    resolveChipsForColor();
    // hero naar de kleurfoto (indien bekend)
    const hero = document.getElementById('pdp-img');
    if (hero && pill.dataset.image) hero.src = pill.dataset.image;
  });
}

function setupFaqToggle() {
  // FAQ: eerste 3 vragen zichtbaar, rest achter t('faq_more') (toggle ↔ t('faq_less'))
  const btn = document.querySelector('[data-faq-toggle]');
  const more = document.getElementById('pdp-faq-more');
  if (!btn || !more) return;
  const label = btn.childNodes[0];
  const count = btn.querySelector('.pdp-faq__count');
  btn.addEventListener('click', () => {
    const open = more.hidden;
    more.hidden = !open;
    btn.setAttribute('aria-expanded', String(open));
    btn.classList.toggle('is-open', open);
    if (label) label.textContent = open ? t('faq_less')+' ' : t('faq_more')+' ';
    if (count) count.hidden = open;
    if (!open) document.querySelector('.pdp-faq')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
}

function setupSticky() {
  const main = document.getElementById('add-to-cart');
  const sticky = document.getElementById('pdp-sticky');
  if (!main || !sticky) return;
  const io = new IntersectionObserver((entries) => {
    sticky.classList.toggle('is-visible', !entries[0].isIntersecting);
  }, { rootMargin: '0px 0px -40% 0px' });
  io.observe(main);
}

// ============================================================
// RELATED — relevance-scored, same category boost
// ============================================================
const ADD_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>';

function renderRelated(all) {
  // Tuinposters: SSR toont al andere tuinposters; de JS-pool bevat ze niet —
  // her-renderen zou er weer papieren posters van maken. Alleen autoscroll aanzetten.
  if ((state.product?.title || '').toLowerCase().startsWith('tuinposter')) {
    const grid0 = document.getElementById('pdp-related-grid');
    if (grid0) setupRelatedAutoScroll(grid0);
    return;
  }
  const grid = document.getElementById('pdp-related-grid');
  const current = state.product;
  if (!grid || !current) return;

  const others = all.filter(x => x.id !== current.id);
  const SPORT = ['voetbal', 'vrouwenvoetbal', 'atleten en sport', 'vechtsport'];
  const curCats = (current.categories || []).map(c => String(c).toLowerCase());
  const curIsSport = curCats.some(c => SPORT.includes(c));
  // Titelwoorden = sterkste relevantie-hint: "Tom Holland" ↔ "Spiderman Tom Holland",
  // "Batman" ↔ "Batman Joker", een acteur ↔ z'n films, etc. Stopwoorden eruit.
  const STOP = new Set(['poster','pop','art','de','het','een','en','van','met','the','of','in','op','a','x','print']);
  const toks = t => String(t || '').toLowerCase().replace(/[^a-z0-9À-ſ ]/g, ' ')
                      .split(/\s+/).filter(w => w.length > 2 && !STOP.has(w));
  const curToks = new Set(toks(current.title));
  const scored = others.map(p => {
    let score = 0;
    const cats = (p.categories || []).map(c => String(c).toLowerCase());
    // gedeelde categorieën wegen zwaar — dat bepaalt "soortgelijk"
    const sharedCats = cats.filter(c => curCats.includes(c)).length;
    score += sharedCats * 4;
    if (p.category && p.category === current.category) score += 3;
    // gedeelde titelwoorden — sterkste signaal voor echte relevantie
    const sharedToks = toks(p.title).filter(w => curToks.has(w)).length;
    score += sharedToks * 6;
    const sharedTags = (p.tags || []).filter(t => (current.tags || []).includes(t)).length;
    score += sharedTags;
    // sport-mismatch: raad geen voetballers aan bij niet-sport designs (en andersom)
    const pIsSport = cats.some(c => SPORT.includes(c));
    if (pIsSport !== curIsSport) score -= 8;
    score += (200 - Math.min(p.bestsellerRank ?? 200, 200)) / 200;
    return { p, score };
  }).filter(x => x.score > 0);
  scored.sort((a, b) => b.score - a.score);
  const picks = scored.slice(0, 6).map(s => s.p);
  if (picks.length < 4) return; // te weinig relevants — behoud server-rendered kaarten

  const cards = picks.map(p => {
    const sale = p.onSale;
    const discount = sale ? Math.round(100 - (p.price / p.originalPrice) * 100) : 0;
    const badge = p.isBestseller ? `<span class="p-card__badge badge-bestseller">${t('badge_bestseller')}</span>`
                : p.isNew ? `<span class="p-card__badge badge-new">${t('badge_new')}</span>`
                : sale ? `<span class="p-card__badge badge-sale">-${discount}%</span>` : '';
    return `
      <div class="p-card-wrap">
        <a class="p-card" href="${LP}/products/${p.id}">
          <div class="p-card__img">
            <img src="${p.image}" alt="${p.title}" loading="lazy" />
            ${badge}
          </div>
          <div class="p-card__body">
            <h3 class="p-card__title">${p.title}</h3>
            <div class="p-card__row">
              <span class="p-card__price tabular">vanaf ${fmt(p.price)}</span>
              <span class="p-card__stars stars">
                <span class="stars__icons">★★★★★</span>
                <span class="tabular">${p.rating}</span>
              </span>
            </div>
          </div>
        </a>
        <button class="p-card__quick" aria-label="${t('card_add_aria', { title: p.title })}" data-quick-add="${p.id}">${ADD_ICON}</button>
      </div>
    `;
  }).join('');
  // Dubbel renderen voor een naadloze loop (bij weinig items zag de harde
  // terugsprong naar 0 er als een glitch uit).
  grid.innerHTML = cards + cards;
  setupRelatedAutoScroll(grid);
}

// Zachte auto-scroll voor de related-strip; pauzeert op hover/touch,
// respecteert prefers-reduced-motion.
let _relRaf = null;
function setupRelatedAutoScroll(strip) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (_relRaf) cancelAnimationFrame(_relRaf); // voorkom dubbele loop na re-render
  strip.style.scrollSnapType = 'none'; // snap vecht met programmatische scroll
  let paused = false, half = 0;
  // iOS rondt scrollLeft af op hele pixels: +=0.5 wordt daar elke frame
  // teruggerond en de strip stond dus stil op mobiel. Sub-pixel accumuleren
  // en alleen hele pixels schrijven.
  let accum = 0;
  let touchHeld = false;
  let userActiveUntil = 0;
  let lastSelfWrite = -1;
  // De content is dubbel gerenderd; op de helft terugzetten valt visueel samen
  // met het beginpunt → naadloze loop zonder de harde terugspring-glitch.
  const recalc = () => { half = strip.scrollWidth / 2; };
  recalc();
  setTimeout(recalc, 300);
  strip.querySelectorAll('img').forEach(img => { if (!img.complete) img.addEventListener('load', recalc, { once: true }); });
  window.addEventListener('resize', recalc);
  const step = () => {
    if (!paused && !touchHeld && Date.now() >= userActiveUntil && half > 0) {
      accum += 0.5;
      if (accum >= 1) {
        const inc = Math.floor(accum);
        accum -= inc;
        strip.scrollLeft += inc;
        if (strip.scrollLeft >= half) strip.scrollLeft -= half;
        lastSelfWrite = strip.scrollLeft;
      }
    }
    _relRaf = requestAnimationFrame(step);
  };
  strip.addEventListener('mouseenter', () => paused = true);
  strip.addEventListener('mouseleave', () => paused = false);
  strip.addEventListener('touchstart', () => { touchHeld = true; userActiveUntil = Date.now() + 2500; }, { passive: true });
  strip.addEventListener('touchend', () => { touchHeld = false; userActiveUntil = Date.now() + 2500; }, { passive: true });
  strip.addEventListener('touchcancel', () => { touchHeld = false; userActiveUntil = Date.now() + 2500; }, { passive: true });
  // Scrolls die we niet zelf schreven (vinger of momentum) verlengen de pauze,
  // zodat autoplay nooit tegen de swipe van de gebruiker in vecht.
  strip.addEventListener('scroll', () => {
    if (Math.abs(strip.scrollLeft - lastSelfWrite) > 3) userActiveUntil = Date.now() + 2500;
  }, { passive: true });
  _relRaf = requestAnimationFrame(step);
}

document.addEventListener('click', (e) => {
  const qa = e.target.closest('[data-quick-add]');
  if (!qa) return;
  e.preventDefault();
  const p = state.allProducts.find(x => x.id === qa.dataset.quickAdd);
  if (!p) return;
  const v = findVariant(p, { size: 'M', material: 'paper' });
  cart.add({
    key:       `${p.id}-${v?.id || 'novariant'}`,
    variantId: v?.id || null,
    title: p.title, image: p.image,
    price: v?.price ?? p.price, qty: 1,
    size: v?.sizeCode || 'M', material: v?.materialCode || 'paper',
  });
  if (window.openCart) window.openCart();
});

// ============================================================
// SCROLL helper voor "Maattabel" link
// ============================================================
document.addEventListener('click', (e) => {
  const t = e.target.closest('[data-action="scroll-spec"]');
  if (!t) return;
  e.preventDefault();
  document.getElementById('pdp-spec')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
});

// ============================================================
// INIT
// ============================================================
// Rating (gevarieerd, synthetisch) — zelfde FNV-hash + verdeling als de
// index-generator, zodat PDP en collectiekaarten exact dezelfde waarde tonen.
// ============================================================
function ratingHash(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return h >>> 0;
}
function genRating(handle) {
  const h = ratingHash(handle), band = h % 100, d = (h >>> 7) % 10;
  let r;
  if (band < 15)      r = 3.0 + d / 10;   // 15% : 3.0-3.9
  else if (band < 97) r = 4.0 + d / 10;   // 82% : 4.0-4.9
  else                r = 5.0;            // 3%  : 5.0
  return Math.round(r * 10) / 10;
}
function renderRating() {
  const el = document.getElementById('pdp-rating');
  const handle = el && el.dataset.handle;
  if (!handle) return;
  // Echte Judge.me-rating (via metafield-sync) heeft voorrang op de synthetische.
  const real = parseFloat(el.dataset.realRating);
  const r = !isNaN(real) ? real : genRating(handle);
  const pct = (r / 5 * 100).toFixed(1);
  el.innerHTML =
    `<span class="rating-stars" style="--pct:${pct}%"><span class="rating-stars__fill">★★★★★</span></span>` +
    `<span class="tabular">${r.toFixed(1)}</span>`;
  el.hidden = false;
}

// ============================================================
(async () => {
  const { product, all } = await loadProduct();
  if (!product) return;
  state.product = product;
  state.allProducts = all;
  readInitialVariant();
  setupSizeChips();
  setupMaterialTiles();
  setupGallery();
  setupQty();
  setupBundleTiers();
  setupFrameUpsell();
  refreshChipPrices();
  setupAddToCart();
  setupSticky();
  setupFaqToggle();
  setupBorderToggle();
  updateBorderToggleVisibility();
  setupColorPills();
  renderRating();
  registerQuickPool(all);
  renderRelated(all); // instant uit de inline-pool
  // Upgrade: scoor de related-sectie uit de VOLLEDIGE catalogus zodat echt
  // relevante posters (bv. "Spiderman Tom Holland" bij "Tom Holland") meedoen,
  // ook als ze niet in de eerste producten van de eigen collecties zaten.
  // Uitgesteld tot de browser idle is: de related-strip staat onder de vouw en
  // de 450KB-fetch + parse mag niet concurreren met de eerste render.
  const upgradeRelated = () => loadAllProducts()
    .then(full => { if (full && full.length > (all?.length || 0)) { registerQuickPool(full); renderRelated(full); } })
    .catch(() => {});
  // 25-09-2026: de volledige catalogus (14 MB) alleen nog op desktop ophalen, en pas
  // als de related-sectie bijna in beeld komt. Op mobiel blijft de inline-pool.
  const relEl = document.querySelector('.pdp-related');
  const isDesktop = window.matchMedia('(min-width: 1024px)').matches
    && !(navigator.connection && navigator.connection.saveData);
  if (isDesktop && relEl && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) { io.disconnect(); upgradeRelated(); }
    }, { rootMargin: '400px 0px' });
    io.observe(relEl);
  }
  // Initiële sync: prijs, doorstreepprijs/pill en free-ship bar afstemmen op de
  // actieve chip (voorheen bleef de SSR-sale van de goedkoopste variant staan).
  const firstChip = document.querySelector('#size-chips .size-chip.is-active:not(.is-pending)')
                 || document.querySelector('#size-chips .size-chip:not(.is-pending)');
  if (firstChip) {
    activateChip(firstChip);
  } else {
    updateFreeShipBar(product.price || 0);
  }
})();

// cache-bust 2026-07-08T23:45 — per-variant sale pill (compare-at + korting%) toegevoegd

// Hero: liggende/vierkante beelden in hun geheel tonen (klasse is-wide, zie pdp.css).
{
  const heroImg = document.getElementById('pdp-img');
  if (heroImg) {
    const zetVorm = () => { if (heroImg.naturalWidth) heroImg.classList.toggle('is-wide', heroImg.naturalWidth / heroImg.naturalHeight > 0.8); };
    heroImg.addEventListener('load', zetVorm);
    if (heroImg.complete) zetVorm();
  }
}

// Hoofdbeeld heeft een srcset (kleinere download op mobiel). Zodra een miniatuur, materiaal of
// kleur het beeld wisselt via .src, moet de srcset weg: anders blijft de browser het oude tonen.
(() => {
  const hero = document.getElementById('pdp-img');
  if (!hero || !hero.hasAttribute('srcset')) return;
  new MutationObserver(() => {
    hero.removeAttribute('srcset');
    hero.removeAttribute('sizes');
  }).observe(hero, { attributes: true, attributeFilter: ['src'] });
})();
